/**
 * inAppBrowser — detects third-party in-app WebViews on iOS.
 *
 * Two families are recognised:
 *
 *   • Social / messaging WebViews (Instagram, FB, TikTok, Snapchat,
 *     LinkedIn, WeChat, Line, Twitter/X, WhatsApp, Discord, Pinterest).
 *
 *   • Mail WebViews (Gmail iOS app, Outlook iOS app, Yahoo Mail,
 *     ProtonMail, Apple Mail's inline HTML preview) — these are the
 *     ones triggered when users tap CTA links inside emails.
 *
 * WHY we care:
 *
 *   1. These WebViews block iOS custom URI scheme handoffs
 *      (`allurexp://...`) and iOS Universal Links, so the Viva
 *      Wallet Apple Pay return strands users on a blank page.
 *
 *   2. Apple only lets users install a PWA to the Home Screen from
 *      real Mobile Safari — not from Chrome-iOS, Firefox-iOS, or any
 *      in-app browser. So users who tap a link inside Gmail and
 *      create an account never see the "Add to Home Screen" flow
 *      and effectively lose access to the app the moment they close
 *      the mail webview.
 *
 * We use this signal to:
 *   • Show a full-screen "Open in Safari" nudge on iOS marketing
 *     routes when we detect any in-app WebView.
 *   • Skip the `allurexp://` handoff on payment callback pages.
 *   • Suppress the "Add to Home Screen" card unless we're in real
 *     Mobile Safari (Home-Screen install is Safari-only on iOS).
 */

// ---------- User-Agent needle lists ----------

// Social / messaging in-app WebViews.
const SOCIAL_UA_NEEDLES = [
  'Instagram',
  'FBAN',
  'FBAV',
  'FB_IAB',
  'BytedanceWebview',
  'musical_ly',
  'Snapchat',
  'LinkedInApp',
  'MicroMessenger',
  'Line/',
  'Twitter',
  'WhatsApp',
  'Discord',
  'Pinterest',
];

// Mail-client in-app WebViews. These are the ones that trap users
// arriving from Zoho / Gmail / Outlook outreach emails.
const MAIL_UA_NEEDLES = [
  'GSA/',              // Google Search / Gmail iOS in-app browser
  'Gmail',             // Some Gmail iOS versions include this token
  'OPT/',              // Outlook iOS ("OPT/x.y.z")
  'Outlook-iOS',
  'OutlookMobile',
  'YahooMobileMail',
  'ProtonMail',
];

// iOS-only alternate browsers. On iOS every browser is forced to use
// WebKit, so they still advertise "Safari/…". We detect them via the
// distinguishing tokens below.
const NON_SAFARI_IOS_NEEDLES = [
  'CriOS',    // Chrome iOS
  'FxiOS',    // Firefox iOS
  'EdgiOS',   // Edge iOS
  'OPiOS',    // Opera iOS
  'YaBrowser', // Yandex
  'DuckDuckGo',
  'Brave',
];

const getUA = () => {
  if (typeof navigator === 'undefined') return '';
  return navigator.userAgent || '';
};

/** True inside a social / messaging in-app WebView. */
export function isSocialWebview() {
  const ua = getUA();
  if (!ua) return false;
  return SOCIAL_UA_NEEDLES.some((n) => ua.includes(n));
}

/** True inside a mail-client in-app WebView (Gmail, Outlook, etc). */
export function isMailWebview() {
  const ua = getUA();
  if (!ua) return false;
  return MAIL_UA_NEEDLES.some((n) => ua.includes(n));
}

/**
 * True inside ANY known third-party in-app WebView (social or mail).
 * Kept as the default export because most callers just need
 * "is this a place where deep links / A2HS won't work?".
 */
export function isInAppBrowser() {
  return isSocialWebview() || isMailWebview();
}

/** True when the device is an iPhone / iPad / iPod. */
export function isIOS() {
  const ua = getUA();
  if (!ua) return false;
  // iPadOS 13+ reports as Mac; detect via touch points too.
  const isMacTouch =
    ua.includes('Mac') &&
    typeof navigator !== 'undefined' &&
    (navigator.maxTouchPoints || 0) > 1;
  return /iPad|iPhone|iPod/.test(ua) || isMacTouch;
}

/** True when the device is Android. */
export function isAndroid() {
  const ua = getUA();
  return /Android/.test(ua);
}

/**
 * True only when this is *real* Mobile Safari on iOS — i.e. not
 * Chrome-iOS / Firefox-iOS / Edge-iOS / any in-app WebView. This is
 * the ONLY environment where "Add to Home Screen" actually works.
 */
export function isRealSafariIOS() {
  if (!isIOS()) return false;
  if (isInAppBrowser()) return false;
  const ua = getUA();
  if (NON_SAFARI_IOS_NEEDLES.some((n) => ua.includes(n))) return false;
  // Real Safari always contains "Safari/" but so do the in-app browsers
  // we've already filtered out above, so this is a good final check.
  return /Safari\//.test(ua);
}

/**
 * True when it's safe to fire an `allurexp://` custom URI scheme
 * handoff from the current page. Returns FALSE inside in-app
 * WebViews (which would swallow the intent and strand the user
 * on a blank page).
 */
export function canHandoffToNativeScheme() {
  return !isInAppBrowser();
}
